import { faPlus, faQuestionCircle, faCode, faBold, faItalic, faStrikethrough, faList, faListOl, faEye, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useRef, useState, useEffect } from 'react';
import './style.css';

function Templatebody({
  bodymessage,
  handleChange,
  bodymessageerror,
  bodymessagetouched,
  handleBlur,
  setFieldValue,
  variable,
  setvariable,
  exampleValues,
  setExampleValues,
  advancedOptions
}) {
  const bodytext = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipContent, setTooltipContent] = useState('');
  const [showFormatGuide, setShowFormatGuide] = useState(false);
  const [activeFormatting, setActiveFormatting] = useState({
    bold: true,
    italic: false,
    strikethrough: false,
    code: false,
    list: false,
    orderedList: false
  });

  // Update example values when variables change, with safeguard against infinite loops
  useEffect(() => {
    // Only run if variable.data exists and has changed
    if (!variable?.data) return;
    
    // Create a copy to prevent direct state mutation
    const newExampleValues = { ...exampleValues };
    let hasChanges = false;
    
    variable.data.forEach(num => {
      if (!newExampleValues[num]) {
        newExampleValues[num] = `Value ${num}`;
        hasChanges = true;
      }
    });
    
    // Only update if something actually changed to prevent infinite loop
    if (hasChanges) {
      setExampleValues(newExampleValues);
    }
  }, [variable?.data]); // Only depend on variable.data, not exampleValues

  // Extract variables from text
  const extractVariables = (text) => {
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    let numbers = matches.map(v => parseInt(v.replace(/[{}]/g, ''))).sort((a, b) => a - b);
    
    // Ensuring uniqueness in numbers array
    numbers = [...new Set(numbers)];
    return numbers;
  };

  // Update variables when text changes
  const updateVariables = (text) => {
    const numbers = extractVariables(text);
    setvariable({ data: numbers });
  };

  // Function to remove a variable and renumber remaining variables
  const removeVariable = (numToRemove) => {
    if (!variable?.data?.includes(numToRemove)) return;
    
    // Create a mapping from old to new numbers
    const mapping = {};
    let newCounter = 1;
    
    // Sort numbers to ensure consistent renumbering
    variable.data.sort((a, b) => a - b).forEach(num => {
      if (num !== numToRemove) {
        mapping[num] = newCounter++;
      }
    });
    
    // Replace all variables in the text with their new numbers
    let updatedText = bodymessage;
    const variableRegex = /\{\{(\d+)\}\}/g;
    updatedText = updatedText.replace(variableRegex, (match, numStr) => {
      const num = parseInt(numStr);
      if (num === numToRemove) {
        return ''; // Remove this variable
      } else {
        return `{{${mapping[num]}}}`;
      }
    });
    
    // Update example values to match new variable numbers
    const newExampleValues = {};
    Object.keys(exampleValues).forEach(key => {
      const num = parseInt(key);
      if (num !== numToRemove && mapping[num]) {
        newExampleValues[mapping[num]] = exampleValues[num];
      }
    });
    
    setFieldValue('bodymessage', updatedText);
    setExampleValues(newExampleValues);
    updateVariables(updatedText);
  };

  const insertVariable = () => {
    const match = bodymessage.match(/\{\{(\d+)\}\}/g) || [];
    const numbers = match.map(v => parseInt(v.replace(/[{}]/g, '')));
    const nextNumber = numbers.length ? Math.max(...numbers) + 1 : 1;

    const start = bodytext.current.selectionStart;
    const end = bodytext.current.selectionEnd;
    const before = bodymessage.substring(0, start);
    const after = bodymessage.substring(end);
    const updated = `${before}{{${nextNumber}}}${after}`;

    setFieldValue('bodymessage', updated);
    updateVariables(updated);
    
    // Set focus back to textarea and position cursor after the inserted variable
    setTimeout(() => {
      bodytext.current.focus();
      const newPosition = start + `{{${nextNumber}}}`.length;
      bodytext.current.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleChangeWithVariableUpdate = (e) => {
    const cursorPos = e.target.selectionStart;
    const newText = e.target.value;    
    setFieldValue('bodymessage', newText);
    updateVariables(newText);
  
    setTimeout(() => {
      e.target.focus();
      e.target.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  // Show tooltip for variable on hover
  const handleMouseMove = (e) => {
    if (!bodytext.current) return;
    
    const text = bodymessage;
    const cursorPos = getCursorPositionFromMouse(e);
    
    if (cursorPos === -1) {
      setShowTooltip(false);
      return;
    }
    
    // Check if cursor is on a variable
    const variableRegex = /\{\{(\d+)\}\}/g;
    let match;
    
    while ((match = variableRegex.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      
      if (cursorPos >= start && cursorPos <= end) {
        const varNumber = match[1];
        const varValue = exampleValues[varNumber] || `Variable ${varNumber}`;
        
        setTooltipContent(`Variable {{${varNumber}}}: ${varValue}`);
        setTooltipPosition({ 
          x: e.clientX, 
          y: e.clientY - 30 // Position above cursor
        });
        setShowTooltip(true);
        return;
      }
    }
    
    setShowTooltip(false);
  };
  
  // Helper to get cursor position from mouse event
  const getCursorPositionFromMouse = (e) => {
    if (!bodytext.current) return -1;
    
    const textarea = bodytext.current;
    const textareaRect = textarea.getBoundingClientRect();
    
    // If mouse is outside textarea, return -1
    if (
      e.clientX < textareaRect.left || 
      e.clientX > textareaRect.right || 
      e.clientY < textareaRect.top || 
      e.clientY > textareaRect.bottom
    ) {
      return -1;
    }
    
    // This is a simplified approach - more accurate would require calculating
    // character position based on mouse coordinates, font metrics, etc.
    // For better accuracy, you might need a more complex implementation
    
    // Get approximate cursor position as percentage of textarea width/height
    const xPercent = (e.clientX - textareaRect.left) / textareaRect.width;
    const yPercent = (e.clientY - textareaRect.top) / textareaRect.height;
    
    // Approximate character position based on textarea content
    const lines = bodymessage.split('\n');
    
    // Find approximate line by y percentage
    const lineIndex = Math.floor(yPercent * lines.length);
    const lineIndex2 = Math.min(lineIndex, lines.length - 1);
    
    // Calculate characters before this line
    let charsBefore = 0;
    for (let i = 0; i < lineIndex2; i++) {
      charsBefore += lines[i].length + 1; // +1 for newline
    }
    
    // Add approximate position within line
    const currentLine = lines[lineIndex2] || '';
    const posInLine = Math.floor(xPercent * currentLine.length);
    
    return charsBefore + posInLine;
  };
  
  const insertFormatting = (formatType) => {
    let prefix, suffix;
    
    switch(formatType) {
      case 'bold':
        prefix = '*';
        suffix = '*';
        break;
      case 'italic':
        prefix = '_';
        suffix = '_';
        break;
      case 'strikethrough':
        prefix = '~';
        suffix = '~';
        break;
      case 'code':
        prefix = '```';
        suffix = '```';
        break;
      case 'list':
        prefix = '• ';
        suffix = '\n';
        break;
      case 'orderedList':
        prefix = '1. ';
        suffix = '\n';
        break;
      default:
        return;
    }

    const start = bodytext.current.selectionStart;
    const end = bodytext.current.selectionEnd;
    const before = bodymessage.substring(0, start);
    const selected = bodymessage.substring(start, end);
    const after = bodymessage.substring(end);

    // If no text is selected, just insert the formatting tags
    let updated;
    if (start === end) {
      updated = `${before}${prefix}${suffix}${after}`;
      setFieldValue('bodymessage', updated);
      
      // Position cursor between the tags
      setTimeout(() => {
        bodytext.current.focus();
        const newPosition = start + prefix.length;
        bodytext.current.setSelectionRange(newPosition, newPosition);
      }, 0);
    } else {
      // If text is selected, wrap it with formatting
      if (formatType === 'list' || formatType === 'orderedList') {
        // For lists, add prefix to each line
        const lines = selected.split('\n');
        const formattedLines = lines.map((line, i) => {
          if (formatType === 'orderedList') {
            return `${i + 1}. ${line}`;
          }
          return `• ${line}`;
        }).join('\n');
        updated = `${before}${formattedLines}${after}`;
      } else {
        // For other formatting, simply wrap the text
        updated = `${before}${prefix}${selected}${suffix}${after}`;
      }
      setFieldValue('bodymessage', updated);
      
      // Maintain selection
      setTimeout(() => {
        bodytext.current.focus();
        bodytext.current.setSelectionRange(
          start + prefix.length, 
          start + prefix.length + selected.length
        );
      }, 0);
    }
    
    // Update variables in case the formatting affected any
    updateVariables(updated);
  };

  const checkActiveFormatting = () => {
    if (!bodytext.current) return;
    
    const start = bodytext.current.selectionStart;
    const end = bodytext.current.selectionEnd;
    const selectedText = bodymessage.substring(start, end);
    
    setActiveFormatting({
      bold: selectedText.match(/^\*.*\*$/) !== null,
      italic: selectedText.match(/^_.*_$/) !== null,
      strikethrough: selectedText.match(/^~.*~$/) !== null,
      code: selectedText.match(/^```.*```$/) !== null,
      list: selectedText.includes('• '),
      orderedList: /^\d+\.\s/.test(selectedText)
    });
  };

  // Preview rendered version of current message with variables replaced
  const getPreviewHTML = () => {
    if (!bodymessage) return '';
    
    // Replace variables with example values
    let previewText = bodymessage;
    
    if (exampleValues && Object.keys(exampleValues).length > 0) {
      Object.keys(exampleValues).forEach(key => {
        if (exampleValues[key]) {
          const variablePattern = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
          previewText = previewText.replace(variablePattern, `<span class="text-primary">${exampleValues[key]}</span>`);
        }
      });
    }
    
    // Apply formatting
    previewText = previewText
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<del>$1</del>")
      .replace(/```(.*?)```/g, "<code>$1</code>")
      .replace(/•\s(.*?)(?=(\n|$))/g, "<div class='bullet-item'>• $1</div>")
      .replace(/(\d+)\.\s(.*?)(?=(\n|$))/g, "<div class='number-item'>$1. $2</div>");
    
    // Replace newlines with <br>
    previewText = previewText.replace(/\n/g, "<br>");
    
    return previewText;
  };
  
  // Toggle format guide popup
  const toggleFormatGuide = () => {
    setShowFormatGuide(!showFormatGuide);
  };

  return (
    <div>
      <div className="form-group m-0 mb-1">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label htmlFor="exampleTextarea1" className="d-flex align-items-center">
            Message Body
            <FontAwesomeIcon 
              icon={faQuestionCircle} 
              className="ml-2 text-muted"
              style={{ cursor: 'pointer' }}
              onClick={toggleFormatGuide}
            />
          </label>
          <span className="text-muted" style={{fontSize: '12px'}}>
            {bodymessage ? bodymessage.length : 0}/1024
          </span>
        </div>
        
        {showFormatGuide && (
          <div className="format-guide card p-3 mb-3">
            <h6 className="mb-2">Text Formatting Guide</h6>
            <div className="row">
              <div className="col-md-6">
                <ul className="pl-3 mb-0">
                  <li><strong>*bold*</strong> = Bold text</li>
                  <li><em>_italic_</em> = Italic text</li>
                  <li><del>~strikethrough~</del> = Strikethrough</li>
                </ul>
              </div>
              <div className="col-md-6">
                <ul className="pl-3 mb-0">
                  <li><code>```code```</code> = Code/monospace</li>
                  <li>• item = Bullet list</li>
                  <li>1. item = Numbered list</li>
                </ul>
              </div>
            </div>
            <button className="btn btn-sm btn-outline-secondary mt-2" onClick={toggleFormatGuide}>
              Close
            </button>
          </div>
        )}
        
        <div className="formatting-toolbar d-flex mb-2">
          <button 
            type="button" 
            className={`btn btn-sm ${activeFormatting.bold ? 'btn-primary' : 'btn-light'} mr-1`}
            title="Bold"
            onClick={() => insertFormatting('bold')}
          >
            <FontAwesomeIcon icon={faBold} />
          </button>
          <button 
            type="button" 
            className={`btn btn-sm ${activeFormatting.italic ? 'btn-primary' : 'btn-light'} mr-1`}
            title="Italic"
            onClick={() => insertFormatting('italic')}
          >
            <FontAwesomeIcon icon={faItalic} />
          </button>
          <button 
            type="button" 
            className={`btn btn-sm ${activeFormatting.strikethrough ? 'btn-primary' : 'btn-light'} mr-1`}
            title="Strikethrough"
            onClick={() => insertFormatting('strikethrough')}
          >
            <FontAwesomeIcon icon={faStrikethrough} />
          </button>
          <button 
            type="button" 
            className={`btn btn-sm ${activeFormatting.code ? 'btn-primary' : 'btn-light'} mr-1`}
            title="Code"
            onClick={() => insertFormatting('code')}
          >
            <FontAwesomeIcon icon={faCode} />
          </button>
          
          <button 
            type="button" 
            className={`btn btn-sm ${activeFormatting.list ? 'btn-primary' : 'btn-light'} mr-1`}
            title="Bullet List"
            onClick={() => insertFormatting('list')}
          >
            <FontAwesomeIcon icon={faList} />
          </button>
          <button 
            type="button" 
            className={`btn btn-sm ${activeFormatting.orderedList ? 'btn-primary' : 'btn-light'} mr-1`}
            title="Numbered List"
            onClick={() => insertFormatting('orderedList')}
          >
            <FontAwesomeIcon icon={faListOl} />
          </button>
          
          <button
            type="button"
            className="btn btn-sm btn-outline-primary ml-auto"
            onClick={insertVariable}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add Variable
          </button>
        </div>
        
        <div className="message-editor-container">
          <textarea
            className="form-control custom-textarea"
            id="exampleTextarea1"
            rows="10"
            name="bodymessage"
            style={{ resize: 'none' }}
            ref={bodytext}
            value={bodymessage || ''}
            onChange={handleChangeWithVariableUpdate}
            onBlur={handleBlur}
            onSelect={checkActiveFormatting}
            onKeyUp={checkActiveFormatting}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setShowTooltip(false)}
            placeholder="Type your message here. Use *text* for bold, _text_ for italic, ~text~ for strikethrough."
          />
          
          {showTooltip && (
            <div 
              className="variable-tooltip bg-dark text-white p-2 rounded"
              style={{
                position: 'fixed',
                top: tooltipPosition.y + 'px',
                left: tooltipPosition.x + 'px',
                transform: 'translate(-50%, -100%)',
                zIndex: 1000,
                fontSize: '0.8rem',
                pointerEvents: 'none'
              }}
            >
              {tooltipContent}
            </div>
          )}
        </div>
      </div>

      {bodymessageerror && bodymessagetouched ? (
        <span className="text-danger" style={{ fontSize: '13px' }}>
          {bodymessageerror}
        </span>
      ) : null}
    </div>
  );
}

export default memo(Templatebody);